import { Endpoint } from '@square/ignition';
import {
  CodeExampleLanguage,
  ReferenceExampleScenario,
} from '@squareup/dex-types-oas';
import { CodeBlockEndpointTitle } from '@squareup/dex-ui';
import { Paragraph20 } from '@squareup/dex-ui-shared-base';
import React, {
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { ReferenceCodeBlock } from '../ReferenceCodeBlock';
import { ReferenceCodeExampleAsideLayout } from '../ReferenceCodeExampleAsideLayout';
import { ReferenceCodeExampleSwitcher } from '../ReferenceCodeExampleSwitcher';

interface ReferenceCodeExampleAsideContainerProps {
  versionSwitcher: ReactNode;
  endpoint: Endpoint;
  codeLanguage: CodeExampleLanguage;
  setCodeLanguage: (value: CodeExampleLanguage) => void;
  scenarios: ReferenceExampleScenario[];
}

const ReferenceCodeExampleAsideContainer: FunctionComponent<
  ReferenceCodeExampleAsideContainerProps
> = ({
  versionSwitcher,
  endpoint,
  scenarios,
  codeLanguage,
  setCodeLanguage,
}) => {
  const defaultExample = {
    name: '',
    code: '',
    response: endpoint?.requestBody?.example || '',
  };

  const [currentExample, setCurrentExample] =
    useState<ReferenceExampleScenario>(scenarios[0] || defaultExample);

  const onExampleScenarioChanged = useCallback(
    (value: ReferenceExampleScenario) => {
      setCurrentExample(value);
    },
    []
  );

  useEffect(() => {
    // In case we switch languages, we need to keep the right scenario
    function findCorrectExample(): ReferenceExampleScenario {
      const foundScenario = scenarios.find(
        (scenario) => scenario.name === currentExample.name
      );
      if (foundScenario) {
        return foundScenario;
      } else {
        return scenarios[0] || defaultExample;
      }
    }

    const exampleToSet = findCorrectExample();
    setCurrentExample(exampleToSet);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeLanguage, scenarios]);

  return (
    <ReferenceCodeExampleAsideLayout
      exampleSwitcher={
        scenarios.length > 1 && (
          <ReferenceCodeExampleSwitcher
            selected={currentExample}
            scenarios={scenarios}
            onScenarioChanged={onExampleScenarioChanged}
          />
        )
      }
      versionSwitcher={versionSwitcher}
      requestCodeBlock={
        <ReferenceCodeBlock
          title={<CodeBlockEndpointTitle endpoint={endpoint} />}
          code={currentExample.code}
          currentLanguage={codeLanguage}
          onLanguageChanged={setCodeLanguage}
          isRequest={true}
        />
      }
      responseCodeBlock={
        <ReferenceCodeBlock
          title={<Paragraph20 weight="semi-bold">Response JSON</Paragraph20>}
          code={currentExample.response}
          currentLanguage={'json'}
          entity={endpoint.responseBody}
          enableAttributeTooltip={true}
        />
      }
    ></ReferenceCodeExampleAsideLayout>
  );
};

export { ReferenceCodeExampleAsideContainer };
